import { RJSFSchema, UiSchema } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { merge, omit, pick } from 'lodash'
import { useState } from 'react'

import Form, { extractUiSchema } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Form'
import proxyAction from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/globalActions/proxy.json'
import useDatasource from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useDatasource'
import useSearchParams from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useSearchParams'
import { Datasource } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/interfaces'
import { requiredActionKeys } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/utilities/constants'

type MetadataProps = {
  onChange: (key: string) => void
}
export function Metadata({ onChange }: MetadataProps) {
  const [datasource, setDatasource] = useDatasource(true)

  const [formErrors, setFormErrors] = useState<{ key: string }>()
  const datasourceEventActions: typeof datasource.events = datasource.events

  const [searchParams, setSearchParams] = useSearchParams()

  const activeEventActionKey = searchParams.get('eventAction')!

  const activeEventAction = datasourceEventActions[activeEventActionKey]

  const handleChange = (data: { key: string } & Pick<Datasource['events'][string], 'name' | 'description'>) => {
    if (!activeEventActionKey || !data.key) return
    if (activeEventActionKey !== data.key && datasourceEventActions[data.key]) {
      setFormErrors({
        key: 'Action with this key already exists.'
      })
      return
    } else {
      setFormErrors(undefined)
    }
    setDatasource(datasource => ({
      ...datasource,
      events: {
        ...omit(datasource.events, [activeEventActionKey]),
        [data.key]: {
          ...(data.key === '_proxy' ? proxyAction : {}),
          ...datasource.events[activeEventActionKey],
          ...pick(data, [
            'name',
            'description',
            'idField',
            'createdDateField',
            'updatedDateField',
            'eventType',
            'dateFormat',
            'timeZone'
          ])
        }
      }
    }))

    setSearchParams(new URLSearchParams({ eventAction: data.key }))
    onChange(data.key)
  }

  const schema: RJSFSchema = {
    type: 'object',
    properties: {
      key: {
        type: 'string',
        title: 'Key'
      },
      name: {
        type: 'string',
        title: 'Name',
        default: activeEventActionKey,
        description:
          'The key is used to identify the event for a datasource trigger. DO NOT change this value if the event action is already in use.'
      },
      description: {
        type: 'string',
        title: 'Description'
      },
      eventType: {
        type: 'string',
        default: 'polling',
        title: 'Event Type',
        enum: ['polling']
      },
      idField: {
        type: 'string',
        title: 'ID Field',
        description: 'The field that uniquely identifies the record.'
      },
      createdDateField: {
        type: 'string',
        title: 'Created Date Field',
        description:
          'The field that represents the date the record was created. Used to infer if the event is of type "created"'
      },
      updatedDateField: {
        type: 'string',
        title: 'Updated Date Field',
        description:
          'The field that represents the date the record was updated. Used to infer if the event is of type "updated"'
      },
      dateFormat: {
        type: 'string',
        title: 'Date Format',
        description: 'The format of the date fields returned from the datasource actions',
        enum: [
          "yyyy-MM-dd\\'T\\'HH:mm:ss.SSSXXX",
          "yyyy-MM-dd'T'HH:mm:ssXXX",
          "yyyy-MM-dd'T'HH:mm:ssZ",
          "yyyy-MM-dd'T'HH:mm:ss'Z'",
          'YYYY-MM-DDTHH:mm:ss.sssZ',
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss',
          'yyyy/MM/dd HH:mm:ss',
          'yyyy/MM/dd HH:mm',
          "MM/dd/yyyy'T'HH:mm:ssXXX",
          'MM/dd/yyyy',
          "dd/MM/yyyy'T'HH:mm:ssXXX",
          'dd/MM/yyyy',
          'EEE, dd MMM yyyy HH:mm:ss Z'
        ],
        default: "yyyy-MM-dd\\'T\\'HH:mm:ss.SSSXXX"
      },
      timeZone: {
        type: 'string',
        title: 'Time Zone',
        description: 'The time zone of the date fields returned from the datasource actions. Most usually UTC',
        enum: [...Intl.supportedValuesOf('timeZone'), 'UTC'],
        default: 'UTC'
      }
    },
    required: ['name', 'eventType', 'idField', 'createdDateField', 'updatedDateField', 'dateFormat', 'timeZone']
  }

  const uiSchema: UiSchema<any, typeof schema> = merge(extractUiSchema(schema), {
    'ui:options': {
      submitButtonOptions: {
        norender: true
      }
    },
    key: {
      'ui:options': {
        disabled: requiredActionKeys.has(activeEventActionKey),
        help: requiredActionKeys.has(activeEventActionKey) && 'Required action keys cannot be changed'
      }
    }
  })

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <p className="mt-1 h-[5px] text-sm text-red-500">{formErrors?.key}</p>
      <Form
        className="flex flex-1 flex-col"
        validator={validator}
        schema={schema}
        uiSchema={uiSchema}
        formData={{
          key: activeEventActionKey,
          ...pick(activeEventAction, [
            'name',
            'description',
            'idField',
            'eventType',
            'createdDateField',
            'updatedDateField',
            'dateFormat',
            'timeZone'
          ])
        }}
        onChange={event => handleChange(event.formData)}
      />
    </div>
  )
}
